import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { JobRunStatus } from "~/common/types/graphql";
import {
  CancelDbpjKitWorkerJobRunDocument,
  type CancelDbpjKitWorkerJobRunMutation,
  type CancelDbpjKitWorkerJobRunMutationVariables,
} from "~/features/dbpjkitWorkerJobs/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

// Cnut's job name for the DBPJKit worker job (see DBPJKitWorkerJobService.jobName).
// cnutUpdateJobRun resolves the run by looking up the job service with this name.
const DBPJKIT_WORKER_JOB_NAME = "DBPJKitWorkerJob";

/**
 * Abort a running DBPJKit worker job by writing `canceled` to its run via the
 * shared `cnutUpdateJobRun` mutation. The job's cooperative-cancellation loop
 * picks the status up at the next batch boundary, cleans up `__WIP` markers, and
 * finalizes the run as `canceled`. Confirmation is the caller's AlertDialog;
 * `onCancel` resolves `true` on success so the caller can close it.
 */
export function useDbpjkitWorkerJobRunCancellation() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  // `canceling` stays true through awaitRefetchQueries, so callers can keep the abort
  // control disabled until the run's new status is reflected (guards against duplicate sends).
  const [cancelJobRun, { loading: canceling }] = useMutation<
    CancelDbpjKitWorkerJobRunMutation,
    CancelDbpjKitWorkerJobRunMutationVariables
  >(CancelDbpjKitWorkerJobRunDocument);

  const onCancel = useCallback(
    async (jobRunId: string) => {
      if (!tenantId) throw new Error("Tenant ID is required");
      try {
        const { data } = await cancelJobRun({
          variables: {
            input: {
              tenantId,
              jobName: DBPJKIT_WORKER_JOB_NAME,
              jobRunId,
              status: JobRunStatus.Canceled,
            },
          },
          // Refresh the latest-run badge (detail) and the run-history table once the status flips.
          refetchQueries: ["GetDBPJKitWorkerJobs", "GetDBPJKitWorkerJobRuns"],
          awaitRefetchQueries: true,
        });
        // cnutUpdateJobRun returns a union; surface ValidationError so the toast shows it.
        const result = data?.cnutUpdateJobRun;
        if (result?.__typename === "ValidationError") {
          throw new Error(result.fields.map((field) => field.message).join(", ") || t("error.unknown"));
        }
        toast.success(t("feature.common.canceledSuccessfully", { model: "dbpjkitWorkerJob" }));
        return true;
      } catch (error) {
        toast.error(translateError(t, error));
        return false;
      }
    },
    [cancelJobRun, t, tenantId],
  );

  return { onCancel, canceling };
}
